import React from 'react';

import classNames from 'classnames';

import ActualsDashboardTile from './ActualsDashboardTile';

import { formatToFixedMoneyWithLocale } from 'Helpers/formatService';

import LoadingOverlay from 'CommonComponents/LoadingOverlay';

const EMPTY_GROUP = { amount: 0, number: 0 };

/**
 * Value and item counts by type, as the dashboard's first tile.
 *
 * Reads the page's own totals rather than the dashboard payload: those totals
 * honour the Month / G-L filter and the dashboard's do not, so sourcing them
 * here would leave a filtered page showing an unfiltered table.
 *
 * Unreconciled and Reconciled values click through to that tab, filtered to the
 * row's type. Item counts and the Total column do not — there is no single tab
 * a total belongs to.
 */
export default function ActualsSummaryTile({ totals, loading, onNavigate }) {
    const row = (key) => totals?.[key] ?? {};
    const figures = (key, group) => row(key)[group] ?? EMPTY_GROUP;

    const ROWS = [
        { key: 'revenue', label: $t('Revenue'), type: 'revenue' },
        { key: 'expenditure', label: $t('Expenditure'), type: 'expenditure' },
        { key: 'total', label: $t('Total'), type: null, isTotal: true },
    ];

    const GROUP_HEADERS = [$t('Unreconciled'), $t('Reconciled'), $t('Total')];

    const cell = (isTotal) => classNames('tw-py-2.5 tw-px-3', !isTotal && 'tw-border-b tw-border-charcoal-15');

    const valueCell = (rowKey, group, type, isTotal) => {
        const amount = formatToFixedMoneyWithLocale(figures(rowKey, group).amount);

        // The Total column has no tab of its own, so it stays plain text.
        if (group === 'total') {
            return <td className={classNames(cell(isTotal), 'tw-text-end', isTotal ? 'tw-font-semibold' : 'tw-font-normal')}>{amount}</td>;
        }

        return (
            <td className={classNames(cell(isTotal), 'tw-text-end')}>
                <button
                    type="button"
                    className={classNames('tw-text-blue hover:tw-underline', isTotal ? 'tw-font-semibold' : 'tw-font-medium')}
                    onClick={() => onNavigate(group, type)}
                >
                    {amount}
                </button>
            </td>
        );
    };

    return (
        <ActualsDashboardTile title={$t('Actuals summary')} className="md:tw-col-span-2 xl:tw-col-span-3">
            <div className="tw-relative tw-overflow-x-auto">
                <LoadingOverlay isVisible={loading} />
                <table className="tw-w-full tw-border-collapse tw-text-xs">
                    <thead>
                        <tr>
                            <th className="tw-w-1/4" />
                            {GROUP_HEADERS.map((label) => (
                                <th key={label} colSpan={2} className="tw-pb-1 tw-text-center tw-font-semibold tw-text-charcoal">
                                    <span className="tw-inline-block tw-border-b-2 tw-border-blue-25 tw-px-1.5 tw-pb-1">{label}</span>
                                </th>
                            ))}
                        </tr>
                        <tr>
                            <th className="tw-border-b tw-border-charcoal-15" />
                            {GROUP_HEADERS.map((label) => (
                                <React.Fragment key={label}>
                                    <th className="tw-border-b tw-border-charcoal-15 tw-py-2 tw-px-3 tw-text-end tw-font-medium tw-text-charcoal-75">
                                        {$t('Value')}
                                    </th>
                                    <th className="tw-border-b tw-border-charcoal-15 tw-py-2 tw-px-3 tw-text-end tw-font-medium tw-text-charcoal-75">
                                        {$t('Items')}
                                    </th>
                                </React.Fragment>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {ROWS.map(({ key, label, type, isTotal }) => (
                            <tr key={key}>
                                <td className={classNames(cell(isTotal), isTotal ? 'tw-font-semibold' : 'tw-font-normal')}>{label}</td>
                                {['unreconciled', 'reconciled', 'total'].map((group) => (
                                    <React.Fragment key={group}>
                                        {valueCell(key, group, type, isTotal)}
                                        <td className={classNames(cell(isTotal), 'tw-text-end tw-text-charcoal-75')}>{figures(key, group).number}</td>
                                    </React.Fragment>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </ActualsDashboardTile>
    );
}
